/**
 * @fileoverview SubmissionSuccess Component - Post-submission options for incident reports
 * Displays options after successfully submitting an incident report.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React from 'react';
import { 
  View, 
  Flex, 
  Button, 
  Heading, 
  Text,
  IconCheckLine,
  IconEyeLine,
  IconXLine
} from '@smarterservices/smarter-ui';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';
import { useDraggableModalContext } from '@ui/shared/components/Common/DraggableModal';
import CONSTANTS from '../../../config/constants';

/**
 * SubmissionSuccess Component
 * 
 * Displays confirmation and next-step options after incident report submission:
 * - Mark session as reviewed
 * - Return to view the submitted report
 * - Close the incident report window
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onMarkAsReviewed - Callback to mark session as reviewed
 * @param {Function} props.onViewReport - Callback to return to viewing the report
 * @param {Function} props.onClose - Callback to close the incident report window
 * @returns {JSX.Element} The rendered submission success interface
 */
function SubmissionSuccess({ onMarkAsReviewed, onViewReport }) {
  // Access modal context for closing
  const modal = useDraggableModalContext();
  
  return (
    <View as="div" padding="medium" textAlign="center">
      {/* Success Header */}
      <View as="div" margin="none none large none">
        <View as="div" margin="none none small none">
          <IconCheckLine size="large" color="success" />
        </View>
        <Heading level="h3" margin="none none small none">
          Incident Report Submitted
        </Heading>
        <Text as="p" color="secondary">
          What do you want to do next?
        </Text>
      </View>

      {/* Action Options */}
      <View as="div" margin="large none none none">
        <Flex direction="column" gap="small" alignItems="center">
          
          {/* Mark Session as Reviewed */}
          <Flex.Item width="100%" maxWidth="300px">
            <NavigationalToolTip
              description="Mark Session as Reviewed"
              shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.SESSION?.OPTIONS?.MARK_REVIEWED?.KEY}
            >
              <Button
                color="success"
                size="medium"
                onClick={onMarkAsReviewed}
                width="100%"
                renderIcon={IconCheckLine}
              >
                Mark Session as Reviewed
              </Button>
            </NavigationalToolTip>
          </Flex.Item>

          {/* View Report */}
          <Flex.Item width="100%" maxWidth="300px">
            <Button
              color="primary"
              size="medium"
              onClick={onViewReport}
              width="100%"
              renderIcon={IconEyeLine}
            >
              View Incident Report
            </Button>
          </Flex.Item>

          {/* Close Window */}
          <Flex.Item width="100%" maxWidth="300px">
            <Button
              color="secondary"
              size="medium"
              onClick={modal.close}
              width="100%"
              renderIcon={IconXLine}
            >
              Close
            </Button>
          </Flex.Item>

        </Flex>
      </View>
    </View>
  );
}

export default SubmissionSuccess;
